import React, { useEffect, useState } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useTranslation } from '@/store/languageStore';
import {
  FLEET_COLUMN_LABEL_KEYS,
  FLEET_COLUMN_SEARCH_EXTRA,
  FLEET_CONFIGURABLE_COLUMN_KEYS,
  FleetConfigurableColumnKey,
  FleetOverviewColumnPreferences,
  getDefaultFleetColumnPreferences,
} from '@/types/fleetColumnPreferences';

interface FleetColumnSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  preferences: FleetOverviewColumnPreferences;
  saving: boolean;
  onSave: (prefs: FleetOverviewColumnPreferences) => Promise<void>;
  onReset: () => Promise<void>;
}

const FleetColumnSettingsModal: React.FC<FleetColumnSettingsModalProps> = ({
  isOpen,
  onClose,
  preferences,
  saving,
  onSave,
  onReset,
}) => {
  const { t } = useTranslation();
  const [draftOrder, setDraftOrder] = useState<FleetConfigurableColumnKey[]>([]);
  const [resetting, setResetting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDraftOrder([...preferences.order]);
      setSearch('');
    }
  }, [isOpen, preferences.order]);

  if (!isOpen) return null;

  const allKeys = [...FLEET_CONFIGURABLE_COLUMN_KEYS];
  const hiddenKeys = allKeys.filter((k) => !draftOrder.includes(k));

  const normalizedSearch = search.trim().toLowerCase();
  const isSearching = normalizedSearch.length > 0;
  const matchesSearch = (key: FleetConfigurableColumnKey) => {
    const label = t(FLEET_COLUMN_LABEL_KEYS[key]).toLowerCase();
    const extra = (FLEET_COLUMN_SEARCH_EXTRA[key] || '').toLowerCase();
    return label.includes(normalizedSearch) || extra.includes(normalizedSearch);
  };
  const searchResults = isSearching ? allKeys.filter(matchesSearch) : [];

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const next = [...draftOrder];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDraftOrder(next);
  };

  const toggleVisible = (key: FleetConfigurableColumnKey, visible: boolean) => {
    if (visible) {
      if (draftOrder.includes(key)) return;
      setDraftOrder([...draftOrder, key]);
      return;
    }
    setDraftOrder(draftOrder.filter((k) => k !== key));
  };

  const handleSave = async () => {
    if (draftOrder.length === 0) return;
    await onSave({ order: draftOrder });
    onClose();
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await onReset();
      setDraftOrder([...getDefaultFleetColumnPreferences().order]);
    } finally {
      setResetting(false);
    }
  };

  const renderSearchRow = (key: FleetConfigurableColumnKey) => {
    const visible = draftOrder.includes(key);
    return (
      <li
        key={key}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bgContainer"
      >
        <input
          type="checkbox"
          checked={visible}
          onChange={(e) => toggleVisible(key, e.target.checked)}
          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
          aria-label={t(FLEET_COLUMN_LABEL_KEYS[key])}
        />
        <span className="flex-1 text-sm text-textDefault">{t(FLEET_COLUMN_LABEL_KEYS[key])}</span>
        <span className="text-xs text-textTertiary">
          {visible ? t('fleet.columnSettings.visibleColumns') : t('fleet.columnSettings.hiddenColumns')}
        </span>
      </li>
    );
  };

  const renderHiddenRow = (key: FleetConfigurableColumnKey) => (
    <li
      key={key}
      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bgContainer"
    >
      <input
        type="checkbox"
        checked={false}
        onChange={(e) => toggleVisible(key, e.target.checked)}
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
        aria-label={t(FLEET_COLUMN_LABEL_KEYS[key])}
      />
      <span className="flex-1 text-sm text-textDefault">{t(FLEET_COLUMN_LABEL_KEYS[key])}</span>
    </li>
  );

  const renderVisibleRow = (key: FleetConfigurableColumnKey, index: number) => (
    <Draggable key={key} draggableId={key} index={index}>
      {(provided, snapshot) => (
        <li
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border bg-bgContainer ${
            snapshot.isDragging
              ? 'border-primary shadow-md ring-1 ring-primary/30'
              : 'border-border'
          }`}
        >
          <button
            type="button"
            {...provided.dragHandleProps}
            className="p-1 rounded cursor-grab active:cursor-grabbing text-textTertiary hover:text-textSecondary hover:bg-bgHover touch-none"
            title={t('fleet.columnSettings.dragHint')}
            aria-label={t('fleet.columnSettings.dragHint')}
          >
            <Bars3Icon className="h-4 w-4" />
          </button>
          <input
            type="checkbox"
            checked
            onChange={(e) => toggleVisible(key, e.target.checked)}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            aria-label={t(FLEET_COLUMN_LABEL_KEYS[key])}
          />
          <span className="flex-1 text-sm text-textDefault">{t(FLEET_COLUMN_LABEL_KEYS[key])}</span>
        </li>
      )}
    </Draggable>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div
        className="bg-bgElevated rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col border border-border"
        role="dialog"
        aria-labelledby="fleet-column-settings-title"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 id="fleet-column-settings-title" className="text-lg font-semibold text-textDefault">
            {t('fleet.columnSettings.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-bgHover text-textSecondary"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto flex-1 space-y-4">
          <p className="text-sm text-textSecondary">{t('fleet.columnSettings.description')}</p>
          <p className="text-xs text-textTertiary">{t('fleet.columnSettings.fixedHint')}</p>

          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textTertiary" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('fleet.columnSettings.searchPlaceholder')}
              className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-border bg-bgContainer text-textDefault focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={t('fleet.columnSettings.searchPlaceholder')}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-bgHover text-textTertiary"
                aria-label={t('common.clear')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {isSearching ? (
            searchResults.length > 0 ? (
              <ul className="space-y-1.5">{searchResults.map((key) => renderSearchRow(key))}</ul>
            ) : (
              <p className="text-sm text-textTertiary text-center py-4">
                {t('fleet.columnSettings.noResults')}
              </p>
            )
          ) : (
            <>
              <div>
                <h3 className="text-xs font-medium uppercase tracking-wide text-textTertiary mb-2">
                  {t('fleet.columnSettings.visibleColumns')}
                </h3>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="fleet-visible-columns">
                    {(provided) => (
                      <ul
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="space-y-1.5"
                      >
                        {draftOrder.map((key, index) => renderVisibleRow(key, index))}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>

              {hiddenKeys.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-textTertiary mb-2">
                    {t('fleet.columnSettings.hiddenColumns')}
                  </h3>
                  <ul className="space-y-1.5">{hiddenKeys.map((key) => renderHiddenRow(key))}</ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => void handleReset()}
            disabled={saving || resetting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-textSecondary hover:text-textDefault rounded-lg hover:bg-bgHover disabled:opacity-50"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {t('fleet.columnSettings.reset')}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-bgHover text-textDefault"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || draftOrder.length === 0}
              className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetColumnSettingsModal;
